import { isWeekend } from "date-fns";
import { call, select } from "redux-saga/effects";

import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { Activity } from "@mapmycustomers/shared/types/entity";
import LeaderboardItem from "@mapmycustomers/shared/types/entity/LeaderboardItem";
import Organization from "@mapmycustomers/shared/types/Organization";
import Team from "@mapmycustomers/shared/types/Team";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import CheckInsCardData from "@app/scene/dashboard/store/cards/checkIns/CheckInsCardData";
import { callApi } from "@app/store/api/callApi";
import { getOrganizationId } from "@app/store/iam";
import { getTeamsOfEntireOrganization } from "@app/store/members";
import CheckInsCardConfiguration from "@app/types/dashboard/cards/checkIns/CheckInsCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { doesWeekStartFromMonday, getLocalTimeZone } from "@app/util/dates";
import { parseApiDate } from "@app/util/parsers";

import { granularityByDateRange } from "./common";

interface ReliabilityCountAggregationItem {
  key: number; // date in numeric format
  secondary: { doc_count: number; key: number }[]; // key === 1 for verified and === 0 for unverified
}

export function* fetchCheckInsCardDataHelper(params: {
  callback: (data: CheckInsCardData) => void;
  configuration: CheckInsCardConfiguration;
  scope?: Scope;
}) {
  const { callback, configuration, scope } = params;
  const { criteria, dateRange, preferences } = configuration;
  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const orgId: Organization["id"] = yield select(getOrganizationId);

  const granularity = granularityByDateRange[dateRange.range];
  const range = getDatesRange(dateRange.range, dateRange.subRange);

  const teams: Team[] = yield select(getTeamsOfEntireOrganization);
  const scopeTeam = isTeamScope ? teams.find(({ id }) => id === scope.teamId) : undefined;
  const assigneeIds = scope?.userId ? [scope.userId] : scopeTeam?.users.map(({ id }) => id);

  const filters = {
    assigneeId: assigneeIds ? { [PlatformFilterOperator.CONTAINS]: assigneeIds } : undefined,
    completedAt: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() },
    crmActivityTypeId: criteria.activityTypeIds.length
      ? { $in: criteria.activityTypeIds }
      : undefined,
    reliability: { $ne: null },
    source: criteria.sources.length ? { $in: criteria.sources } : undefined,
  };

  const payload = {
    $aggs: {
      count: ["id"],
      primaryGroup: {
        field: "completedAt",
        interval: granularity,
        offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
        timezone: getLocalTimeZone(),
      },
      secondaryGroup: {
        field: "reliability",
      },
    },
    $filters: filters,
    $limit: 0,
  };

  const response: AggregatedListResponse<Activity, ReliabilityCountAggregationItem[]> =
    yield callApi("fetchActivities", orgId, payload);

  const history: CheckInsCardData["history"] = response.aggregations
    .map((item) => ({
      date: parseApiDate(item.key),
      unverifiedCount: criteria.includeUnverified
        ? item.secondary.find(({ key }) => key === 0)?.doc_count
        : undefined,
      verifiedCount: item.secondary.find(({ key }) => key === 1)?.doc_count ?? 0,
    }))
    .filter(
      ({ date, unverifiedCount, verifiedCount }) =>
        !isWeekend(date) ||
        verifiedCount + (preferences.showUnverified && unverifiedCount ? unverifiedCount : 0) > 0
    );

  const result: CheckInsCardData = {
    history: preferences.showChart ? history : undefined,
    verifiedCount: 0,
  };

  result.verifiedCount = history.reduce((sum, item) => sum + item.verifiedCount, 0) ?? 0;
  if (criteria.includeUnverified) {
    result.unverifiedCount = history.reduce((sum, item) => sum + (item.unverifiedCount ?? 0), 0);
  }

  if (result.history && preferences.cumulative) {
    let verifiedSum = 0;
    let unverifiedSum = 0;
    result.history = result.history.map((item) => {
      verifiedSum += item.verifiedCount;
      unverifiedSum += item.unverifiedCount ?? 0;
      return { ...item, unverifiedCount: unverifiedSum, verifiedCount: verifiedSum };
    });
  }

  if (preferences.showTrend) {
    // fetch again, but for the previous range
    const previousRange = getDatesRange(dateRange.range, dateRange.subRange, true);
    const previousRangePayload = {
      ...payload,
      $filters: {
        ...filters,
        completedAt: {
          $gte: previousRange.startDate.toISOString(),
          $lte: previousRange.endDate.toISOString(),
        },
      },
    };
    const response: AggregatedListResponse<Activity, ReliabilityCountAggregationItem[]> =
      yield callApi("fetchActivities", orgId, previousRangePayload);

    // if we include unverified check-ins, we can just take the total number
    // otherwise, we need to calculate total ourselves using only doc_count for
    // verified checkins
    result.previousCount = criteria.includeUnverified
      ? response.total
      : response.aggregations
          .flatMap((item) => item.secondary.filter((subitem) => subitem.key === 1))
          .reduce((sum, item) => sum + item.doc_count, 0);

    if (preferences.showChart) {
      result.previousHistory = response.aggregations
        .map((item) => ({
          date: parseApiDate(item.key),
          unverifiedCount: criteria.includeUnverified
            ? item.secondary.find(({ key }) => key === 0)?.doc_count
            : undefined,
          verifiedCount: item.secondary.find(({ key }) => key === 1)?.doc_count ?? 0,
        }))
        .filter(
          ({ date, unverifiedCount, verifiedCount }) =>
            !isWeekend(date) ||
            verifiedCount + (preferences.showUnverified && unverifiedCount ? unverifiedCount : 0) >
              0
        );

      if (result.previousHistory && preferences.cumulative) {
        let verifiedSum = 0;
        let unverifiedSum = 0;
        result.previousHistory = result.previousHistory.map((item) => {
          verifiedSum += item.verifiedCount;
          unverifiedSum += item.unverifiedCount ?? 0;
          return { ...item, unverifiedCount: unverifiedSum, verifiedCount: verifiedSum };
        });
      }
    }
  }

  // if we should show the rank and scope is not the Entire Org
  if (preferences.showRankPosition && (scope?.teamId || scope?.userId)) {
    const response: ListResponse<LeaderboardItem> = yield callApi(
      "fetchLeaderboardData",
      orgId,
      {
        $and: [
          ...(criteria.activityTypeIds.length
            ? [{ crmActivityTypeId: { $in: criteria.activityTypeIds } }]
            : []),
        ],
        date: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() },
        includePerformanceMetric: true,
        property: { $in: [LeaderboardMetricFieldName.CHECK_INS_COMPLETED] },
        summary: isTeamScope ? "team" : "user",
      },
      undefined
    );
    const rank = isTeamScope
      ? response.data.findIndex(({ teamId }) => teamId === scope!.teamId)
      : response.data.findIndex(({ userId }) => userId === scope!.userId);
    result.rank = rank >= 0 ? rank + 1 : undefined;
  }

  yield call(callback, result);
}
